'use client';
import React, { FC } from 'react';
import { ExternalLink } from '../ExternalLink';
import * as Contentful from 'contentful';
import { TypeLink_externalFields } from '@/core/generated-types';
import { useCuralateScript } from '@/hooks/useCuralateScript';
import { getShopifyDomain, SHOPIFY_DOMAIN } from '@/core/constants';

export interface CuralateRegionalConfig {
  id: string;
  script: string;
}

export const CRL8_REGIONAL_CONFIGS: Record<
  SHOPIFY_DOMAIN,
  CuralateRegionalConfig
> = {
  [SHOPIFY_DOMAIN.AU]: {
    id: 'instashop-homepage-KYxjAYDB',
    script: `var CRL8_SITENAME = 'loveveryaustralia-ewkj4k'; !function(){var e=window.crl8=window.crl8||{},n=!1,i=[];e.ready=function(e){n?e():i.push(e)},e.pixel=e.pixel||function(){e.pixel.q.push(arguments)},e.pixel.q=e.pixel.q||[];var t=window.document,o=t.createElement("script"),c=e.debug||-1!==t.location.search.indexOf("crl8-debug=true")?"js":"min.js";o.async=!0,o.src=t.location.protocol+"//edge.curalate.com/sites/"+CRL8_SITENAME+"/site/latest/site."+c,o.onload=function(){n=!0,i.forEach(function(e){e()})};var r=t.getElementsByTagName("script")[0];r.parentNode.insertBefore(o,r.nextSibling)}();`,
  },
  [SHOPIFY_DOMAIN.EU]: {
    id: 'instashop-other-LrYdbacn',
    script: `var CRL8_SITENAME = 'loveveryeurope-34l48j'; !function(){var e=window.crl8=window.crl8||{},n=!1,i=[];e.ready=function(e){n?e():i.push(e)},e.pixel=e.pixel||function(){e.pixel.q.push(arguments)},e.pixel.q=e.pixel.q||[];var t=window.document,o=t.createElement("script"),c=e.debug||-1!==t.location.search.indexOf("crl8-debug=true")?"js":"min.js";o.async=!0,o.src=t.location.protocol+"//edge.curalate.com/sites/"+CRL8_SITENAME+"/site/latest/site."+c,o.onload=function(){n=!0,i.forEach(function(e){e()})};var r=t.getElementsByTagName("script")[0];r.parentNode.insertBefore(o,r.nextSibling)}();`,
  },
  [SHOPIFY_DOMAIN.UK]: {
    id: 'instashop-homepage-78yXNRM1',
    script: `var CRL8_SITENAME = 'loveveryeurope-34l48j'; !function(){var e=window.crl8=window.crl8||{},n=!1,i=[];e.ready=function(e){n?e():i.push(e)},e.pixel=e.pixel||function(){e.pixel.q.push(arguments)},e.pixel.q=e.pixel.q||[];var t=window.document,o=t.createElement("script"),c=e.debug||-1!==t.location.search.indexOf("crl8-debug=true")?"js":"min.js";o.async=!0,o.src=t.location.protocol+"//edge.curalate.com/sites/"+CRL8_SITENAME+"/site/latest/site."+c,o.onload=function(){n=!0,i.forEach(function(e){e()})};var r=t.getElementsByTagName("script")[0];r.parentNode.insertBefore(o,r.nextSibling)}();`,
  },
  [SHOPIFY_DOMAIN.US]: {
    id: 'homepage',
    script: `var CRL8_SITENAME = 'lovevery-ondspy';!function(){var e=window.crl8=window.crl8||{},n=!1,i=[];e.ready=function(e){n?e():i.push(e)},e.pixel=e.pixel||function(){e.pixel.q.push(arguments)},e.pixel.q=e.pixel.q||[];var t=window.document,o=t.createElement("script"),c=e.debug||-1!==t.location.search.indexOf("crl8-debug=true")?"js":"min.js";o.async=!0,o.src=t.location.protocol+"//edge.curalate.com/sites/"+CRL8_SITENAME+"/site/latest/site."+c,o.onload=function(){n=!0,i.forEach(function(e){e()})};var r=t.getElementsByTagName("script")[0];r.parentNode.insertBefore(o,r.nextSibling)}();`,
  },
};

export interface SocialFeedProps {
  feedTitle?: string;
  feedLink?: Contentful.Entry<TypeLink_externalFields>;
}

const SocialFeed: FC<SocialFeedProps> = ({ feedTitle, feedLink }) => {
  const storeDomain = getShopifyDomain();
  const defaultConfig = CRL8_REGIONAL_CONFIGS[SHOPIFY_DOMAIN.US];
  const regionConfig = CRL8_REGIONAL_CONFIGS[storeDomain] || defaultConfig;

  useCuralateScript(regionConfig);

  return (
    <div className="bg-aqua-tint py-10 text-center lg:py-20">
      <div className="container mx-auto px-2 lg:px-0">
        {feedTitle && (
          <h2
            className="title-container mb-2 text-h2 font-bold md:text-h1 lg:mb-4"
            data-testid="feed-title"
          >
            {feedTitle}
          </h2>
        )}
        {feedLink && (
          <ExternalLink link={feedLink} classes="justify-center text-indigo" />
        )}
        <div
          className="mt-1 lg:mt-6"
          data-crl8-container-id={regionConfig.id}
          data-testid="feed-container"
        />
      </div>
    </div>
  );
};

export default React.memo(SocialFeed);
